import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { ResultSectionWithTitle } from './ResultSectionEmpty';
import { Activity, MessageSquare, ChevronUp, ChevronDown } from 'lucide-react';

interface AclsResultProps {
	resultMetadata: unknown;
	isAllParticipantsView?: boolean;
}

/** ACLS (전문소생술) 평가 결과 */
export default function AclsResult({ resultMetadata, isAllParticipantsView = false }: AclsResultProps): React.ReactElement {
	const { t } = useTranslation('class');
	const { t: tScenario } = useTranslation('scenario');
	const title = t('simulationResults.tablePerformance');
	const m = resultMetadata as Record<string, unknown> | null | undefined;

	// 전체 참가자 뷰에서는 card별 토글 상태 관리 불필요
	const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

	const translationPrefix = 'study_result.acls';

	// card 토글 함수 (개별 참가자 뷰에서만 사용)
	const toggleCard = (groupIdx: number, cardIdx: number) => {
		if (isAllParticipantsView) return; // 전체 참가자 뷰에서는 토글 불가
		const key = `${groupIdx}-${cardIdx}`;
		setExpandedCards((prev) => {
			const next = new Set(prev);
			if (next.has(key)) {
				next.delete(key);
			} else {
				next.add(key);
			}
			return next;
		});
	};

	const isCardExpanded = (groupIdx: number, cardIdx: number) => {
		if (isAllParticipantsView) return false; // 전체 참가자 뷰에서는 항상 닫힘
		return expandedCards.has(`${groupIdx}-${cardIdx}`);
	};

	if (m == null || typeof m !== 'object') {
		return (
			<ResultSectionWithTitle title={title} lead="activity">
				<div className="text-gray-500 text-sm">데이터를 불러오는 중입니다...</div>
			</ResultSectionWithTitle>
		);
	}

	const evaluation = m.aclsEvaluation as Record<string, unknown> | undefined;
	if (!evaluation || typeof evaluation !== 'object') {
		return (
			<ResultSectionWithTitle title={title} lead="activity">
				<div className="text-gray-500 text-sm">평가 데이터가 없습니다.</div>
			</ResultSectionWithTitle>
		);
	}

	const groups = evaluation.group as Array<Record<string, unknown>> | undefined;
	if (!Array.isArray(groups) || groups.length === 0) {
		return (
			<ResultSectionWithTitle title={title} lead="activity">
				<div className="text-gray-500 text-sm">평가 데이터가 없습니다.</div>
			</ResultSectionWithTitle>
		);
	}

	return (
		<div className="space-y-6 !mt-6 !mb-4">
			<h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-2.5 min-w-0">
				<div className="w-9 h-9 flex items-center justify-center rounded-lg border shrink-0 bg-slate-200/60 border-slate-200/80">
					<Activity size={20} className="text-slate-600" />
				</div>
				<span className="truncate min-w-0">{title}</span>
			</h3>

			<div className="rounded-xl border border-slate-200/90 overflow-hidden bg-white shadow-sm overflow-x-auto">
				<table className="w-full text-sm text-center table-fixed">
					<thead>
						<tr className="bg-gray-50 border-b border-gray-200">
							<th className="px-4 py-3 font-semibold text-gray-700 text-center w-12">No</th>
							<th className="px-4 py-3 font-semibold text-gray-700 text-start w-40">평가항목</th>
							<th className="px-4 py-3 font-semibold text-gray-700 text-start">내용</th>
							<th className="px-4 py-3 font-semibold text-gray-700 text-center w-20">
								{isAllParticipantsView ? '정답율' : '결과'}
							</th>
							{!isAllParticipantsView && (
								<th className="px-4 py-3 font-semibold text-gray-700 text-center w-20">코멘트</th>
							)}
						</tr>
					</thead>
					<tbody className="divide-y divide-gray-200">
						{groups.map((group, groupIdx) => {
							const groupKey = group.key as string || '';
							const groupName = group.name as string || '';

							const cards = group.cards as Array<Record<string, unknown>> | undefined;
							if (!Array.isArray(cards) || cards.length === 0) return null;

							return (
								<React.Fragment key={groupIdx}>
									{cards.map((card, cardIdx) => {
										const items = card.items as Array<Record<string, unknown>> | undefined;
										if (!Array.isArray(items) || items.length === 0) return null;

										const debriefingKey = (card.debriefing_key as string) || '';
										const debriefing = (card.debriefing as string) || '';
										const isExpanded = isCardExpanded(groupIdx, cardIdx);
										
										// 전체 참가자 뷰: card.accuracy 사용
										const accuracy = isAllParticipantsView ? (card.accuracy as number) : null;

										return (
											<React.Fragment key={cardIdx}>
												<tr className={`transition-colors ${isExpanded ? 'bg-gray-50' : 'hover:bg-gray-50'}`}>
													{/* No: 첫 번째 card에만 표시 (rowSpan) */}
													{cardIdx === 0 && (
														<td className="px-4 py-3 text-center text-gray-500 font-medium bg-gray-50/50 border-r border-gray-200" rowSpan={cards.length}>
															{groupIdx + 1}
														</td>
													)}
													{/* 평가항목: 첫 번째 card에만 표시 (rowSpan) */}
													{cardIdx === 0 && (
														<td className="px-4 py-3 text-start text-gray-900 font-medium text-sm bg-gray-50/50 border-r border-gray-200" rowSpan={cards.length}>
															{groupName || <Trans i18nKey={`${translationPrefix}.${groupKey}`} ns="scenario" />}
														</td>
													)}
													{/* 내용 */}
													<td className="px-4 py-3 text-start align-top">
														<div className="space-y-3">
															{items.map((item, itemIdx) => {
																const pass = item.pass as boolean;
																const itemKey = (item.key as string) || '';

																return (
																	<div key={itemIdx}>
																		{/* 항목 */}
																		<div className={`leading-relaxed text-sm ${
																			isAllParticipantsView 
																				? 'text-gray-700' 
																				: pass ? 'text-gray-700' : 'text-red-600 font-medium'
																		}`}>
																			<Trans i18nKey={`${translationPrefix}.${itemKey}`} ns="scenario" />
																		</div>
																	</div>
																);
															})}
															{/* 디브리핑 (개별 참가자 뷰에서만, 확장 시 표시) */}
															{!isAllParticipantsView && isExpanded && debriefingKey && (
																<div className="mt-3 pt-3 border-t border-gray-200 -mx-4 px-4">
																	<div className="bg-white p-4 rounded-lg border border-gray-200">
																		<div className="flex items-start gap-3">
																			<div className="mt-0.5 p-1.5 bg-indigo-50 rounded-full text-indigo-600 flex-shrink-0">
																				<MessageSquare size={16} />
																			</div>
																			<div className="flex-1 min-w-0 text-sm text-gray-600 leading-relaxed">
																				{debriefing ? (
																					<div dangerouslySetInnerHTML={{ __html: debriefing }} />
																				) : (
																					<Trans i18nKey={`${translationPrefix}.${debriefingKey}.Debriefing`} ns="scenario" />
																				)}
																			</div>
																		</div>
																	</div>
																</div>
															)}
														</div>
													</td>
													{/* 결과 */}
													<td className="px-4 py-3 text-center align-top" style={{ verticalAlign: 'top' }}>
														{isAllParticipantsView ? (
															<span className="text-lg font-bold text-emerald-600">
																{accuracy != null ? `${accuracy.toFixed(1)}%` : '—'}
															</span>
														) : (
															items.every(item => item.pass === true) ? (
																<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">
																	{tScenario('gcsEvaluationResult.resultCorrect')}
																</span>
															) : items.every(item => item.pass === false) ? (
																<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700">
																	{tScenario('gcsEvaluationResult.resultWrong')}
																</span>
															) : (
																<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-700">
																	일부
																</span>
															)
														)}
													</td>
													{/* 코멘트 토글 (개별 참가자 뷰에서만) */}
													{!isAllParticipantsView && (
														<td className="px-4 text-center relative align-top" style={{ verticalAlign: 'top' }}>
															<div className="absolute top-3 left-1/2 -translate-x-1/2 flex items-center" style={{ height: '24px' }}>
																{debriefingKey ? (
																	<div
																		className={`h-8 w-8 p-0 inline-flex items-center justify-center rounded-md transition-all cursor-pointer ${
																			isExpanded ? 'bg-gray-200' : ''
																		}`}
																		onClick={() => toggleCard(groupIdx, cardIdx)}
																		role="button"
																		aria-label={isExpanded ? t('simulationResults.collapseComment') : t('simulationResults.expandComment')}
																	>
																		{isExpanded ? (
																			<ChevronUp size={14} className="text-gray-600" />
																		) : (
																			<ChevronDown size={14} className="text-gray-500" />
																		)}
																	</div>
																) : (
																	<span className="text-gray-300 text-sm">—</span>
																)}
															</div>
														</td>
													)}
												</tr>
											</React.Fragment>
										);
									})}
								</React.Fragment>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
}
